import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import DateInput from './DateInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Plus, Trash2, PoundSterling, DollarSign, Euro } from 'lucide-react';

const RATE_TYPES = [
  { value: 'standard', label: 'Standard Rate' },
  { value: 'travel', label: 'Travel Day' },
  { value: 'standby', label: 'Standby' },
  { value: 'holiday', label: 'Holiday/Weekend Rate' },
  { value: 'uplift', label: 'Uplift Period' },
  { value: 'other', label: 'Other' },
];

const CURRENCIES = [
  { value: 'GBP', label: 'GBP (£)', symbol: '£', icon: PoundSterling },
  { value: 'USD', label: 'USD ($)', symbol: '$', icon: DollarSign },
  { value: 'EUR', label: 'EUR (€)', symbol: '€', icon: Euro },
];

const DayRatesSection = ({ 
  dayRates, 
  addDayRate, 
  updateDayRate, 
  removeDayRate,
  formData,
  setFormData,
  dateContext = ''
}) => {
  // Get date range from form data
  const getDateRange = () => {
    const start = formData.date_left_uk || formData.date_joined_vessel;
    const end = formData.date_returned_uk || formData.date_left_vessel;
    return { start, end };
  };

  const { start, end } = getDateRange();
  
  // Get currency info
  const currency = formData.currency || 'GBP';
  const currencyInfo = CURRENCIES.find(c => c.value === currency) || CURRENCIES[0];
  const CurrencyIcon = currencyInfo.icon;

  // Calculate total earnings from all periods
  const calculateTotalEarnings = () => {
    return dayRates.reduce((sum, r) => {
      if (r.date_from && r.date_to && r.daily_rate) {
        const days = Math.ceil((new Date(r.date_to) - new Date(r.date_from)) / (1000 * 60 * 60 * 24)) + 1;
        return sum + (days * parseFloat(r.daily_rate || 0));
      }
      return sum;
    }, 0);
  };

  return (
    <div className="space-y-4 p-4 bg-amber-500/5 rounded-lg border border-amber-500/20">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CurrencyIcon className="h-4 w-4 text-amber-400" />
          <h3 className="text-sm font-medium text-amber-400">Earnings - Day Rates</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select 
            value={currency} 
            onValueChange={(value) => setFormData({...formData, currency: value})}
          >
            <SelectTrigger className="w-28 bg-slate-800 border-slate-600 text-white text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              {CURRENCIES.map(c => (
                <SelectItem key={c.value} value={c.value} className="text-white">
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addDayRate}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Rate Period
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Add one or more rate periods to calculate earnings. Use different rates for travel days, standard days, holiday uplifts, etc.
        {currency !== 'GBP' && (
          <span className="block mt-1 text-amber-400">
            Paid in {currency} - enter the actual GBP received in Monthly Earnings after payment.
          </span>
        )}
      </p>

      {dayRates.length === 0 ? (
        <div className="text-center py-6 bg-slate-900/30 rounded-lg border border-dashed border-slate-600">
          <p className="text-sm text-slate-500">
            No day rates added yet
          </p>
          <p className="text-xs text-slate-600 mt-1">
            Click "Add Rate Period" to specify your daily rates for this trip
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {dayRates.map((rate, idx) => (
            <div key={rate._key || idx} className="p-3 bg-slate-900/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-amber-400 font-medium">Period {idx + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeDayRate(idx)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">From Date</Label>
                  <DateInput contextDate={dateContext}
                    value={rate.date_from || ''}
                    onChange={(e) => updateDayRate(idx, 'date_from', e.target.value)}
                    min={start}
                    max={end}
                    className="bg-slate-800 border-slate-600 text-white text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">To Date</Label>
                  <DateInput contextDate={dateContext}
                    value={rate.date_to || ''}
                    onChange={(e) => updateDayRate(idx, 'date_to', e.target.value)}
                    min={rate.date_from || start}
                    max={end}
                    className="bg-slate-800 border-slate-600 text-white text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Rate Type</Label>
                  <Select 
                    value={rate.rate_type || 'standard'} 
                    onValueChange={(value) => updateDayRate(idx, 'rate_type', value)}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      {RATE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Daily Rate ({currencyInfo.symbol})</Label>
                  <Input
                    type="number"
                    value={rate.daily_rate || ''}
                    onChange={(e) => updateDayRate(idx, 'daily_rate', e.target.value)}
                    placeholder={`e.g., ${currency === 'USD' ? '850' : currency === 'EUR' ? '750' : '581'}`}
                    className="bg-slate-800 border-slate-600 text-white text-sm"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Description (optional)</Label>
                <Input
                  value={rate.description || ''}
                  onChange={(e) => updateDayRate(idx, 'description', e.target.value)}
                  placeholder="e.g., Offshore allowance included"
                  className="bg-slate-800 border-slate-600 text-white text-sm"
                />
              </div>

              {/* Show period subtotal */}
              {rate.date_from && rate.date_to && rate.daily_rate && (
                <div className="text-xs text-slate-500 pt-1 border-t border-slate-700">
                  {(() => {
                    const days = Math.ceil((new Date(rate.date_to) - new Date(rate.date_from)) / (1000 * 60 * 60 * 24)) + 1;
                    const subtotal = days * parseFloat(rate.daily_rate);
                    return `${days} days × ${currencyInfo.symbol}${parseFloat(rate.daily_rate).toLocaleString()} = ${currencyInfo.symbol}${subtotal.toLocaleString()}`;
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dayRates.length > 0 && (
        <div className="pt-3 border-t border-amber-500/20">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-400">Total Earnings ({currency}):</span>
            <span className="text-lg font-semibold text-amber-400">
              {currencyInfo.symbol}{calculateTotalEarnings().toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayRatesSection;
